import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useCan } from "@/auth/permissions";
import {
  batchCustomerProducts, getCustomerPrices, listCustomerPartners, putCustomerDefault, SOURCE_TONE,
  type CustomerProductRow,
} from "@/services/customerPrices";
import { PageShell, LoadingState, EmptyState } from "@/shared/ui/PageHeader";
import { useReasonDialog } from "@/shared/ui/ReasonDialog";   // REASON-1
import { PricingSubnav } from "./PricingSubnav";
import { CustomerPriceDrawer } from "./CustomerPriceDrawer";

/**
 * PRICE-CUST-1 · 客户专属价（样稿画面③）：左边选客户（带已设条数）；右边先设「这个客户全部产品」（原客户整体折扣，只有这一套），
 * 再给单个产品设系数 / 固定金额；产品表显示 成本 · 结算价 · 建议售价 · 价格来源；可批量勾选设系数 / 批量恢复跟随。
 * 5 层取价：客户×产品 → 客户全部产品 → 客群 → 本产品单独设 → 全局，越具体越优先、只取一层（同行站只显示价格，不显示来源）。
 */
const usd = (c: number) => `$${(c / 100).toFixed(2)}`;
const SIGNED2 = /^-?\d+(\.\d{1,2})?$/;
const fmtPct = (n: number) => `${n >= 0 ? "+" : "−"}${Math.abs(n)}%`;

export function CustomerPricesPage() {
  const qc = useQueryClient();
  const canEdit = useCan("pricing.channel_config.update");
  const [sp, setSp] = useSearchParams();
  const partnerId = sp.get("partner_id") ?? "";
  const [search, setSearch] = useState("");
  const { ask, dialog: reasonDialog } = useReasonDialog();

  const partnersQ = useQuery({ queryKey: ["pricing", "customer-prices", "partners"], queryFn: () => listCustomerPartners() });
  const partners = partnersQ.data ?? [];
  useEffect(() => {   // 没选客户 → 默认选第一个
    if (!partnerId && partners.length > 0) setSp({ partner_id: partners[0].id }, { replace: true });
  }, [partnerId, partners, setSp]);
  const shown = useMemo(() => partners.filter((p) => !search.trim() || `${p.name_cn} ${p.code}`.toLowerCase().includes(search.trim().toLowerCase())), [partners, search]);

  const ovQ = useQuery({ queryKey: ["pricing", "customer-prices", "overview", partnerId], enabled: !!partnerId, queryFn: () => getCustomerPrices(partnerId) });
  const ov = ovQ.data;

  // 「全部产品」系数草稿
  const [defMode, setDefMode] = useState<"follow" | "custom">("follow");
  const [defPct, setDefPct] = useState("");
  const [defFrom, setDefFrom] = useState("");
  const [defTo, setDefTo] = useState("");
  useEffect(() => {
    const d = ov?.default;
    setDefMode(d ? "custom" : "follow");
    setDefPct(d ? String(d.markup_pct) : "");
    setDefFrom(d?.effective_from ?? ""); setDefTo(d?.effective_to ?? "");
  }, [ov?.partner.id, ov?.default?.markup_pct, ov?.default?.effective_from, ov?.default?.effective_to]);   // eslint-disable-line react-hooks/exhaustive-deps
  const defErr = defMode === "custom" ? (!defPct.trim() ? "请填写系数" : !SIGNED2.test(defPct.trim()) ? "最多两位小数" : Number(defPct) < -50 || Number(defPct) > 50 ? "范围 −50 ~ 50" : null) : null;
  const defChanged = defMode === "follow" ? !!ov?.default : (!ov?.default || Number(defPct) !== ov.default.markup_pct || (defFrom || null) !== ov.default.effective_from || (defTo || null) !== ov.default.effective_to);

  const refresh = () => qc.invalidateQueries({ queryKey: ["pricing", "customer-prices"] });
  const saveDefault = useMutation({
    mutationFn: async () => {
      const got = await ask({
        scene: "price_change", title: "设置客户「全部产品」系数", subtitle: ov?.partner.name_cn,
        changes: [{ label: "全部产品", before: ov?.default ? fmtPct(ov.default.markup_pct) : `跟随客群${ov?.partner.tier_name ? `（${ov.partner.tier_name}）` : ""}`, after: defMode === "follow" ? "跟随客群" : fmtPct(Number(defPct)) }],
        confirmText: "确认并保存",
      });
      if (!got) return false;
      await putCustomerDefault(partnerId, { markup_pct: defMode === "follow" ? null : Number(defPct), effective_from: defFrom || null, effective_to: defTo || null, ...got });
      return true;
    },
    onSuccess: (ok) => { if (ok) refresh(); },
  });

  // 选中 + 批量
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkPct, setBulkPct] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  useEffect(() => { setPicked(new Set()); setBulkOpen(false); }, [partnerId]);
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const rows: CustomerProductRow[] = ov?.products ?? [];
  const allOn = rows.length > 0 && picked.size === rows.length;
  const bulkErr = !bulkPct.trim() ? "请填写系数" : !SIGNED2.test(bulkPct.trim()) ? "最多两位小数" : Number(bulkPct) < -90 || Number(bulkPct) > 500 ? "范围 −90 ~ 500" : null;
  const batch = useMutation({
    mutationFn: async (mode: "markup" | "reset") => {
      const got = await ask({
        scene: "price_change", title: mode === "reset" ? "批量恢复跟随" : "批量设专属系数", subtitle: `${ov?.partner.name_cn} · ${picked.size} 个产品`,
        changes: [{ label: "专属价", before: "各自当前", after: mode === "reset" ? "跟随上一层" : `系数 ${fmtPct(Number(bulkPct))}` }], confirmText: "确认并保存",
      });
      if (!got) return null;
      return batchCustomerProducts(partnerId, { product_ids: [...picked], mode, ...(mode === "markup" ? { markup_pct: Number(bulkPct) } : {}), ...got });
    },
    onSuccess: (r) => { if (r) { setPicked(new Set()); setBulkOpen(false); refresh(); } },
  });

  const [drawer, setDrawer] = useState<string | null>(null);
  const inp = "h-7 w-[72px] rounded-[5px] border px-1.5 text-right text-[12.5px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <PageShell title="客户专属价" subtitle="每个同行客户可以有自己的价：整体系数 / 单个产品系数 / 直接填金额 · 越具体越优先、只取一层">
      <PricingSubnav />
      <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* 左：客户列表 */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="cp-partners">
          <label className="flex items-center gap-1.5 border-b border-slate-200 px-2.5 py-2 text-[12.5px] text-slate-400">
            <Search size={13} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="搜客户名 / 编号" aria-label="搜客户" className="w-full bg-transparent outline-none" data-testid="cp-search" />
          </label>
          {partnersQ.isLoading ? <LoadingState /> : shown.length === 0 ? (
            <div className="p-4 text-center text-[12.5px] text-slate-400">没有找到客户</div>
          ) : (
            <div className="max-h-[560px] overflow-y-auto">
              {shown.map((p) => (
                <button key={p.id} type="button" onClick={() => setSp({ partner_id: p.id })} data-testid={`cp-partner-${p.code}`}
                  className={`flex w-full items-center justify-between gap-1.5 border-b border-slate-100 px-3 py-2 text-left text-[13px] ${p.id === partnerId ? "bg-blue-50 font-semibold text-blue-700" : "text-slate-800 hover:bg-slate-50"}`}>
                  <span>{p.name_cn}</span>
                  <small className="whitespace-nowrap font-normal text-slate-400">{p.code}{p.override_count ? ` · ${p.override_count} 条` : ""}</small>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 右：这个客户 */}
        <div className="flex min-w-0 flex-col gap-3.5">
          {!partnerId ? <EmptyState message="先选一个客户" /> : ovQ.isLoading ? <LoadingState /> : ovQ.isError || !ov ? (
            <button type="button" onClick={() => ovQ.refetch()} className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-left text-[13px] font-medium text-rose-700">加载失败，点击重试</button>
          ) : (
            <>
              <div className="flex flex-col gap-2.5 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-[16px] font-semibold text-slate-900" data-testid="cp-partner-name">{ov.partner.name_cn}</h4>
                  <Link to={`/admin/channel-partners/${ov.partner.id}`} className="text-[12.5px] font-medium text-blue-600 hover:underline">打开同行详情 ›</Link>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-slate-600">
                  <span>客群：{ov.partner.tier_name ? `${ov.partner.tier_name} ${ov.partner.tier_markup_pct != null ? fmtPct(ov.partner.tier_markup_pct) : ""}` : "未设客群（不加价）"}</span>
                  <span>结算：{ov.partner.settlement_mode === "prepaid" ? "预付" : ov.partner.settlement_mode === "credit" ? "月结" : ov.partner.settlement_mode}</span>
                  <span data-testid="cp-count">已设专属价：{ov.partner.override_count} 条</span>
                </div>

                <div className="mt-1 text-[13px] font-semibold text-slate-900">全部产品</div>
                <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-600">
                  <label className={`inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 ${defMode === "follow" ? "border-blue-600 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white"}`}>
                    <input type="radio" className="accent-blue-600" checked={defMode === "follow"} disabled={!canEdit} onChange={() => setDefMode("follow")} data-testid="cp-def-follow" />
                    跟随客群{ov.partner.tier_name ? `（${ov.partner.tier_name} ${ov.partner.tier_markup_pct != null ? fmtPct(ov.partner.tier_markup_pct) : ""}）` : ""}
                  </label>
                  <label className={`inline-flex h-[30px] cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 ${defMode === "custom" ? "border-blue-600 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 bg-white"}`}>
                    <input type="radio" className="accent-blue-600" checked={defMode === "custom"} disabled={!canEdit} onChange={() => setDefMode("custom")} data-testid="cp-def-custom" />
                    单独系数
                    <input aria-label="全部产品系数" data-testid="cp-def-pct" value={defPct} disabled={!canEdit || defMode !== "custom"} onChange={(e) => setDefPct(e.target.value)} inputMode="decimal" placeholder="−3" className={`${inp} ${defErr && defMode === "custom" ? "border-rose-400" : "border-slate-300"}`} />%
                  </label>
                  <span className="text-[11.5px] text-slate-400">在结算价上整体 ± 百分比（原「客户整体折扣」并入这里）</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-600">
                  <span>生效日期</span>
                  <input type="date" aria-label="开始日期" data-testid="cp-def-from" value={defFrom} disabled={!canEdit || defMode !== "custom"} onChange={(e) => setDefFrom(e.target.value)} className="h-7 rounded-[5px] border border-slate-300 px-1.5" />至
                  <input type="date" aria-label="结束日期" data-testid="cp-def-to" value={defTo} disabled={!canEdit || defMode !== "custom"} onChange={(e) => setDefTo(e.target.value)} className="h-7 rounded-[5px] border border-slate-300 px-1.5" />
                  <span className="text-[11.5px] text-slate-400">不填 = 一直有效</span>
                  {defErr && <span className="text-[11.5px] text-rose-600">{defErr}</span>}
                  {canEdit && (
                    <button type="button" onClick={() => saveDefault.mutate()} disabled={!defChanged || !!defErr || saveDefault.isPending} data-testid="cp-def-save" className="ml-auto h-7 rounded-md bg-blue-600 px-3 text-[12.5px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
                      {saveDefault.isPending ? "保存中…" : "保存（弹原因）"}
                    </button>
                  )}
                </div>
                {saveDefault.isError && <div className="text-[12px] text-rose-600" data-testid="cp-def-error">保存失败：{(saveDefault.error as Error).message}</div>}
              </div>

              <div className="flex flex-col gap-2.5 rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-[13px] font-semibold text-slate-900">单个产品 <span className="ml-1 text-[12px] font-normal text-slate-500">共 {rows.length} 个在售产品 · 价格为两人一房 / 每人</span></div>
                </div>
                {canEdit && picked.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-1.5 text-[12.5px] text-blue-700" data-testid="cp-bulk">
                    <span>已选 {picked.size} 个产品</span>
                    {bulkOpen ? (
                      <>
                        <input aria-label="批量系数" data-testid="cp-bulk-pct" value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} inputMode="decimal" placeholder="+8" className={`${inp} border-slate-300 bg-white`} />%
                        {bulkPct && bulkErr && <span className="text-rose-600">{bulkErr}</span>}
                        <button type="button" disabled={!!bulkErr || batch.isPending} onClick={() => batch.mutate("markup")} data-testid="cp-bulk-go" className="h-7 rounded-md bg-blue-600 px-2.5 font-semibold text-white disabled:opacity-40">确认</button>
                        <button type="button" onClick={() => setBulkOpen(false)} className="h-7 rounded-md border border-slate-200 bg-white px-2.5 text-slate-600">取消</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => setBulkOpen(true)} data-testid="cp-bulk-set" className="h-7 rounded-md border border-blue-200 bg-white px-2.5 font-medium">批量设系数</button>
                        <button type="button" onClick={() => batch.mutate("reset")} disabled={batch.isPending} data-testid="cp-bulk-reset" className="h-7 rounded-md border border-blue-200 bg-white px-2.5 font-medium">批量恢复跟随</button>
                      </>
                    )}
                  </div>
                )}
                {batch.isError && <div className="text-[12px] text-rose-600" data-testid="cp-bulk-error">批量保存失败：{(batch.error as Error).message}</div>}
                {rows.length === 0 ? <EmptyState message="没有在售产品" /> : (
                  <div className="overflow-x-auto rounded-[10px] border border-slate-200">
                    <table className="w-full min-w-[640px] text-[12.5px]">
                      <thead className="bg-slate-50 text-left text-slate-600">
                        <tr>
                          <th className="w-8 px-2.5 py-2">{canEdit && <input type="checkbox" aria-label="全选" checked={allOn} onChange={() => setPicked(allOn ? new Set() : new Set(rows.map((r) => r.product_id)))} />}</th>
                          <th className="px-2.5 py-2">产品</th><th className="px-2.5 py-2 text-right">成本</th><th className="px-2.5 py-2 text-right">结算价</th><th className="px-2.5 py-2 text-right">建议售价</th><th className="px-2.5 py-2">价格来源</th><th className="px-2.5 py-2" />
                        </tr>
                      </thead>
                      <tbody data-testid="cp-rows">
                        {rows.map((r) => (
                          <tr key={r.product_id} className="border-t border-slate-100" data-testid={`cp-row-${r.code}`}>
                            <td className="px-2.5 py-2">{canEdit && <input type="checkbox" aria-label={`选择${r.code}`} checked={picked.has(r.product_id)} onChange={() => toggle(r.product_id)} />}</td>
                            <td className="px-2.5 py-2"><b className="font-semibold text-slate-900">{r.code}</b> {r.title}</td>
                            <td className="px-2.5 py-2 text-right tabular-nums">{usd(r.cost_cents)}</td>
                            <td className={`px-2.5 py-2 text-right tabular-nums ${r.below_cost ? "font-semibold text-rose-600" : ""}`}>
                              {usd(r.settlement_cents)}{r.below_cost && <span className="ml-1 rounded-full bg-rose-50 px-1.5 text-[11px] font-semibold text-rose-600" data-testid={`cp-below-${r.code}`}>低于成本</span>}
                            </td>
                            <td className="px-2.5 py-2 text-right tabular-nums">{usd(r.retail_cents)}</td>
                            <td className="px-2.5 py-2"><span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${SOURCE_TONE[r.source.level]}`} data-testid={`cp-source-${r.code}`}>{r.source.label}</span></td>
                            <td className="px-2.5 py-2 text-right">
                              <button type="button" onClick={() => setDrawer(r.product_id)} data-testid={`cp-edit-${r.code}`} className="text-[12.5px] font-medium text-blue-600 hover:underline">{r.override_id ? "改" : canEdit ? "设专属价" : "查看"}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {drawer && partnerId && <CustomerPriceDrawer partnerId={partnerId} productId={drawer} onClose={() => setDrawer(null)} onSaved={refresh} />}
      {reasonDialog}
    </PageShell>
  );
}
